/**
 * 사용자 관리 페이지 (M12 / U3-c) — admin 전용.
 *
 * 기능:
 *   - 사용자 목록 테이블 (가입 순)
 *   - 신규 사용자 생성 모달
 *   - 행별 권한 토글 (user ↔ admin)
 *   - 행별 활성/비활성 토글
 *   - 행별 비밀번호 재설정 모달
 *
 * 보호 정책 (서버 가드와 일치):
 *   - 본인 행의 권한/활성 변경 / 비번 리셋 버튼은 disabled.
 *   - 서버에서 마지막 admin 보호 등으로 400 이 돌아오면 alert 로 메시지 노출.
 */

import { useState } from "react";
import { KeyRound, Loader2, ShieldCheck, ShieldOff, UserPlus, UserX } from "lucide-react";
import { useTranslation } from "react-i18next";

import { useAuthStore } from "@/features/auth/store";
import type { AdminUserOut, UserRole } from "@/lib/api/admin_users";
import { ApiError } from "@/lib/api/client";
import { cn } from "@/lib/cn";

import CreateUserModal from "./CreateUserModal";
import ResetPasswordModal from "./ResetPasswordModal";
import { useAdminUsers, useUpdateAdminUser } from "./useAdminUsers";

/**
 * ISO → 현재 i18n 언어의 short date+time.
 *
 * 기존엔 ``ko-KR`` 로 하드코딩되어 있어 일본어 모드에서도 한국식 날짜
 * 표기 ("2026. 5. 22.") 가 노출됐다. ``i18n.resolvedLanguage`` (ja|ko)
 * 에 따라 ``ja-JP`` ("2026/05/22") / ``ko-KR`` 로 분기.
 */
function useFormatDate() {
  const { i18n } = useTranslation();
  const locale = i18n.resolvedLanguage === "ko" ? "ko-KR" : "ja-JP";
  return (iso: string): string => {
    const d = new Date(iso);
    return Number.isNaN(d.getTime())
      ? iso
      : d.toLocaleString(locale, { dateStyle: "short", timeStyle: "short" });
  };
}

function RoleBadge({ role }: { role: UserRole }) {
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium",
        role === "admin" ? "bg-purple-100 text-purple-700" : "bg-slate-100 text-slate-600",
      )}
    >
      {role}
    </span>
  );
}

function ActiveBadge({ active }: { active: boolean }) {
  const { t } = useTranslation();
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium",
        active ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-500",
      )}
    >
      {active ? t("admin.users.badges.active") : t("admin.users.badges.inactive")}
    </span>
  );
}

export default function UsersPage() {
  const { t } = useTranslation();
  const formatDate = useFormatDate();
  const currentUserId = useAuthStore((s) => s.user?.id);
  const { data, isLoading, error } = useAdminUsers();
  const updateMu = useUpdateAdminUser();

  const [createOpen, setCreateOpen] = useState(false);
  const [resetTarget, setResetTarget] = useState<AdminUserOut | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [busyUserId, setBusyUserId] = useState<number | null>(null);

  const handleToggleRole = (u: AdminUserOut) => {
    if (u.id === currentUserId) return;
    setActionError(null);
    setBusyUserId(u.id);
    updateMu.mutate(
      { userId: u.id, payload: { role: u.role === "admin" ? "user" : "admin" } },
      {
        onError: (err) => {
          setActionError(err instanceof ApiError ? err.detail : err.message);
        },
        onSettled: () => setBusyUserId(null),
      },
    );
  };

  const handleToggleActive = (u: AdminUserOut) => {
    if (u.id === currentUserId) return;
    setActionError(null);
    setBusyUserId(u.id);
    updateMu.mutate(
      { userId: u.id, payload: { is_active: !u.is_active } },
      {
        onError: (err) => {
          setActionError(err instanceof ApiError ? err.detail : err.message);
        },
        onSettled: () => setBusyUserId(null),
      },
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-lg font-semibold text-slate-900">{t("admin.users.title")}</h1>
          <p className="mt-0.5 text-xs text-slate-500">{t("admin.users.description")}</p>
        </div>
        <button
          type="button"
          onClick={() => setCreateOpen(true)}
          className="inline-flex items-center gap-1.5 rounded-md bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm transition hover:bg-blue-700"
        >
          <UserPlus className="h-3.5 w-3.5" />
          {t("admin.users.newUser")}
        </button>
      </div>

      {actionError ? (
        <div
          role="alert"
          className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700"
        >
          {actionError}
        </div>
      ) : null}

      <div className="overflow-hidden rounded-md border border-slate-200 bg-white">
        <table className="min-w-full divide-y divide-slate-200 text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-3 py-2">{t("admin.users.th.id")}</th>
              <th className="px-3 py-2">{t("admin.users.th.email")}</th>
              <th className="px-3 py-2">{t("admin.users.th.role")}</th>
              <th className="px-3 py-2">{t("admin.users.th.status")}</th>
              <th className="px-3 py-2">{t("admin.users.th.password")}</th>
              <th className="px-3 py-2">{t("admin.users.th.createdAt")}</th>
              <th className="px-3 py-2 text-right">{t("admin.users.th.actions")}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {isLoading ? (
              <tr>
                <td colSpan={7} className="px-3 py-10 text-center text-xs text-slate-400">
                  <Loader2 className="mx-auto h-4 w-4 animate-spin" aria-hidden />
                </td>
              </tr>
            ) : error ? (
              <tr>
                <td colSpan={7} className="px-3 py-10 text-center text-xs text-red-500">
                  {t("admin.users.error", { message: error.message })}
                </td>
              </tr>
            ) : !data || data.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-3 py-10 text-center text-xs text-slate-400">
                  {t("admin.users.empty")}
                </td>
              </tr>
            ) : (
              data.map((u) => {
                const isSelf = u.id === currentUserId;
                const isBusy = busyUserId === u.id;
                return (
                  <tr key={u.id} className={cn("text-slate-700", isSelf && "bg-blue-50/40")}>
                    <td className="px-3 py-2 font-mono text-xs text-slate-500">{u.id}</td>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{u.email}</span>
                        {isSelf ? (
                          <span className="rounded-full bg-blue-100 px-1.5 py-0.5 text-[10px] font-medium text-blue-700">
                            {t("admin.users.badges.self")}
                          </span>
                        ) : null}
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      <RoleBadge role={u.role} />
                    </td>
                    <td className="px-3 py-2">
                      <ActiveBadge active={u.is_active} />
                    </td>
                    <td className="px-3 py-2">
                      {u.must_change_password ? (
                        <span className="inline-flex items-center gap-1 text-[11px] font-medium text-amber-700">
                          <KeyRound className="h-3 w-3" />
                          {t("admin.users.password.mustChange")}
                        </span>
                      ) : (
                        <span className="text-[11px] text-slate-400">
                          {t("admin.users.password.normal")}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-xs text-slate-500">{formatDate(u.created_at)}</td>
                    <td className="px-3 py-2">
                      <div className="flex justify-end gap-1">
                        <button
                          type="button"
                          onClick={() => handleToggleRole(u)}
                          disabled={isSelf || isBusy}
                          title={
                            isSelf
                              ? t("admin.users.actions.roleSelfTitle")
                              : u.role === "admin"
                                ? t("admin.users.actions.roleDemoteTitle")
                                : t("admin.users.actions.rolePromoteTitle")
                          }
                          className="inline-flex items-center gap-1 rounded-md border border-slate-300 px-2 py-1 text-[11px] text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
                        >
                          {u.role === "admin" ? (
                            <ShieldOff className="h-3 w-3" />
                          ) : (
                            <ShieldCheck className="h-3 w-3" />
                          )}
                          {u.role === "admin"
                            ? t("admin.users.actions.roleDemote")
                            : t("admin.users.actions.rolePromote")}
                        </button>

                        <button
                          type="button"
                          onClick={() => handleToggleActive(u)}
                          disabled={isSelf || isBusy}
                          title={
                            isSelf
                              ? t("admin.users.actions.activeSelfTitle")
                              : u.is_active
                                ? t("admin.users.actions.deactivateTitle")
                                : t("admin.users.actions.activateTitle")
                          }
                          className="inline-flex items-center gap-1 rounded-md border border-slate-300 px-2 py-1 text-[11px] text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
                        >
                          <UserX className="h-3 w-3" />
                          {u.is_active
                            ? t("admin.users.actions.deactivateLabel")
                            : t("admin.users.actions.activateLabel")}
                        </button>

                        <button
                          type="button"
                          onClick={() => setResetTarget(u)}
                          disabled={isSelf}
                          title={
                            isSelf
                              ? t("admin.users.actions.resetSelfTitle")
                              : t("admin.users.actions.resetTitle")
                          }
                          className="inline-flex items-center gap-1 rounded-md border border-amber-300 px-2 py-1 text-[11px] text-amber-700 transition hover:bg-amber-50 disabled:cursor-not-allowed disabled:opacity-40"
                        >
                          <KeyRound className="h-3 w-3" />
                          {t("admin.users.actions.resetButton")}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {createOpen ? <CreateUserModal onClose={() => setCreateOpen(false)} /> : null}
      {resetTarget ? (
        <ResetPasswordModal user={resetTarget} onClose={() => setResetTarget(null)} />
      ) : null}
    </div>
  );
}
